/**
 * Procedural-fallback colour palette for Phase 1 rendering.
 *
 * Per Q-C8 resolution, the `PackResolver` is internal to P1.7: pack sprite
 * → procedural + theme → procedural + defaults. Since no sprite atlas ships
 * with Phase 1 yet, every surface renders from the procedural layer here.
 * Real art lands in Phase 4 polish.
 *
 * MaterialId / BiomeId values are `u32` newtypes — in practice Phase 1
 * populates them from `data/materials.toml` and the biome classifier
 * respectively, using small integer indices into the catalogue below.
 */

/** Fallback colour used when a MaterialId / BiomeId / PropKind falls off
 *  the end of its palette. Vivid magenta so it's obvious during dev. */
export const PALETTE_FALLBACK_COLOR = 0xff00ff;

/**
 * Material palette (14 slots per WORLD_MODEL §3 / CLIENT_DESIGN §4.2).
 * Index 0 is Grass — the worldgen default — so new empty worlds paint
 * green rather than the fallback magenta.
 */
export const MATERIAL_COLORS: readonly number[] = [
  0x5b8c52, // 0  Grass
  0x7d7a77, // 1  Stone
  0x8b5a2b, // 2  Wood
  0xd1c8b5, // 3  Tile
  0x8b6f47, // 4  Dirt
  0xe6d49a, // 5  Sand
  0x336ea8, // 6  Water
  0x6b5028, // 7  Tilled Soil
  0x707070, // 8  Cobblestone
  0xf0f5fa, // 9  Snow
  0x5d4a33, // 10 Mud
  0xa5836b, // 11 Clay
  0x9d6a5e, // 12 Carpet
  0xe8e5dc, // 13 Marble
  0xb9a182, // 14 Path
];

export function materialColor(id: number): number {
  return MATERIAL_COLORS[id] ?? PALETTE_FALLBACK_COLOR;
}

/**
 * Biome palette — subtle ambient tints applied over the material base.
 * Slight multipliers, not replacements; used for faint desaturation /
 * hue shift (Phase 1.5 autotile blending replaces this with edge overlays).
 */
export const BIOME_TINTS: readonly number[] = [
  0xffffff, // 0  Grassland — no-op tint
  0xd5dbc4, // 1  Forest — greener
  0xbfd6e0, // 2  Coast — bluer
  0xefe1b4, // 3  Desert — yellower
  0xdce7ea, // 4  Tundra — whiter
  0x90a090, // 5  Swamp — muted green
  0xc4bfb5, // 6  Mountain — grey
  0x88adc8, // 7  Ocean — strong blue
];

export function biomeTint(id: number): number {
  return BIOME_TINTS[id] ?? 0xffffff;
}

/** Wall-material palette (Phase 1 ships Wood + Stone — WORLD_MODEL §4.2). */
export const WALL_MATERIAL_COLORS: Record<string, number> = {
  Wood: 0x8b5a2b,
  Stone: 0x7d7a77,
  Plaster: 0xd1c8b5,
  Brick: 0x9c5a48,
  Thatch: 0xc8a655,
  Log: 0x6b4a2b,
};

export function wallMaterialColor(material: string): number {
  return WALL_MATERIAL_COLORS[material] ?? PALETTE_FALLBACK_COLOR;
}

/**
 * Prop kind → colour rectangle. PropKind is a `u32` on the wire (indices
 * into `data/props.toml`); until the pack atlas lands, we render each prop
 * as a small coloured rectangle with the kind printed on it. The mapping
 * below is intentionally coarse — everything collapses to half-a-dozen
 * bucket colours and the label carries the real information.
 */
export function propKindColor(kind: number): number {
  // Stable hash-to-colour so the same PropKind always gets the same swatch.
  // Kept in the "warm-cozy" palette family (CLIENT_DESIGN §1).
  const bucket = kind % 8;
  switch (bucket) {
    case 0:
      return 0x8b5a2b; // brown
    case 1:
      return 0x5b8c52; // green
    case 2:
      return 0xc6a664; // warm yellow
    case 3:
      return 0x8b3a3a; // rust
    case 4:
      return 0x4a5d7a; // muted blue
    case 5:
      return 0x6b5e88; // muted purple
    case 6:
      return 0x9e7b52; // tan
    default:
      return 0x707070; // grey
  }
}
